import type { SubmitEventHandler } from 'react';

import { use, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { useAppForm } from '../../../form-hooks/form';

interface KeyRegenerateModalContentProps {
  expectedKeyName: string;
  testId?: string;
}

const KeyRegenerateModalContent = ({
  expectedKeyName,
  testId,
}: KeyRegenerateModalContentProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const validation = useMemo(
    () =>
      z.object({
        apiKeyName: z
          .string()
          .refine((value) => value.trim() === expectedKeyName, {
            message: t('ApiKeys.RegenerateConfirm.Error'),
          }),
      }),
    [expectedKeyName, t],
  );

  const form = useAppForm({
    defaultValues: { apiKeyName: '' },
    validators: { onSubmit: validation },
    onSubmit: () => modalInstance.resolve?.(true),
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <div
      className="text-slate-900 dark:text-white"
      {...getTestProps(testId, 'confirm-key-modal-container')}
    >
      <form.AppForm>
        <form
          id="confirm-key-modal-form"
          className="w-full flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
          {...getTestProps(testId)}
        >
          <p className="mb-2">
            <Trans i18nKey="ApiKeys.RegenerateConfirm.Info" />
          </p>

          <form.AppField name="apiKeyName">
            {(field) => (
              <field.Input
                label={t('ApiKeys.RegenerateConfirm.InputLabel')}
                additionalClasses="text-left"
                required
                autoFocus
                autoComplete="off"
                spellCheck={false}
                {...getTestProps(testId, 'api-key-name', 'testId')}
              />
            )}
          </form.AppField>

          <p className="mt-2 mb-3">
            <Trans
              i18nKey="ApiKeys.RegenerateConfirm.Description"
              values={{ name: expectedKeyName }}
              components={{
                keyName: (
                  <code className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-sm" />
                ),
              }}
            />
          </p>

          <div
            className={twMerge(
              'flex justify-center gap-3 absolute bottom-3 pt-3',
              'left-1/2 -translate-x-1/2 w-full',
              'border-t border-slate-100 dark:border-slate-800',
            )}
          >
            <Button
              buttonColor="blueBordered"
              type="button"
              onClick={() => modalInstance.resolve?.(false)}
              {...getTestProps(testId, 'cancel', 'testId')}
            >
              {t('Global.Cancel')}
            </Button>

            <Button
              buttonColor="blue"
              type="submit"
              {...getTestProps(testId, 'regenerate', 'testId')}
            >
              {t('ApiKeys.RegenerateConfirm.Regenerate')}
            </Button>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};

export default KeyRegenerateModalContent;
